import { effect, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { Events, EventService } from 'src/app/core/services/event.service';
import { User } from 'src/app/core/models/user';
import { Organization } from 'src/app/core/models/entities';

@Injectable({
  providedIn: 'root',
})
export class WhiteLabelService {
  public readonly logo = signal<string>('');

  constructor(
    private readonly http: HttpClient,
    private readonly eventService: EventService,
    private readonly user: User,
  ) { }

  public uploadLogo(organization_id: number, file: File): Observable<WhiteLabelLogoUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${environment.apiUrl}/v1/organization/whitelabels/${organization_id}/upload-logo/`;
    return this.http.post<WhiteLabelLogoUploadResponse>(url, formData);
  }

  public uploadFavicon(organization_id: number, file: File): Observable<WhiteLabelFaviconUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${environment.apiUrl}/v1/organization/whitelabels/${organization_id}/upload-favicon/`;
    return this.http.post<WhiteLabelFaviconUploadResponse>(url, formData);
  }

  public get(organization_id: number): Observable<WhiteLabelParentResponse> {
    const url = `${environment.apiUrl}/v1/organization/whitelabels/${organization_id}/`;
    return this.http.get<WhiteLabelParentResponse>(url);
  }

  public upsert(organization_id: number, request: WhiteLabelRequest) {
    const url = `${environment.apiUrl}/v1/organization/whitelabels/${organization_id}/`;
    return this.http.put<WhiteLabelParentResponse>(url, request);
  }

  public whiteLabelEffect() {
    effect(() => {
      const orgDropdownEvent = this.eventService.getEvent(Events.OrgDropdownChange.type)();

      if (orgDropdownEvent === null) return;

      this.user.updateUser({
        selected_organization: orgDropdownEvent
      });

      if (this.user.primary_organization?.id === orgDropdownEvent.id) {
        this.user.updateUser({
          primary_organization: orgDropdownEvent
        });
      }

      this.updateWhiteLabelUI(orgDropdownEvent);
    }, { allowSignalWrites: true });

    effect(() => {
      const whiteLabelEvent = this.eventService.getEvent(Events.WhiteLabelChange.type)();

      if (whiteLabelEvent === null) return;

      this.updateWhiteLabelUI(whiteLabelEvent);

    }, { allowSignalWrites: true });
  }

  private updateWhiteLabelUI(org: Organization): void {
    if (!org) return;

    let whitelabel = org.whitelabel?.enabled
      ? org.whitelabel
      : org.parent_whitelabel;

    if (!whitelabel) return;

    this.logo.set(whitelabel.logo_url ?? '../assets/logos/cyflare-one-logo-new-colors.png');

    this.updateFaviconUrl(whitelabel.favicon_url ?? '../favicon.ico');

    document.title = whitelabel.title ?? 'CyFlare ONE';
  }

  private updateFaviconUrl(favicon_url: string | null | undefined) {
    console.log(favicon_url);
    document.querySelector('link[rel="icon"]')?.setAttribute('href', favicon_url ?? '../assets/logos/cyflare-one-logo-new-colors.png');
  }
}

export interface WhiteLabelResponse {
  message?: string;
  url: string | null;
  logo_url: string;
  organization_id: number;
  whitelabel_id?: number;
  enabled?: boolean | null;
  title?: string | null;
  favicon_url: string | null;
}

export interface WhiteLabelLogoUploadResponse {
  message?: string;
  logo_url: string;
  organization_id: number;
  enabled?: boolean | null;
}

export interface WhiteLabelFaviconUploadResponse {
  message?: string;
  favicon_url: string;
  organization_id: number;
  enabled?: boolean | null;
}

export interface WhiteLabelRequest {
  url?: string | null;
  enabled?: boolean | null;
  title?: string | null;
}

export interface WhiteLabelParentResponse {
  parent_whitelabel: WhiteLabelResponse;
  whitelabel: WhiteLabelResponse;
}
